<template>
  <div v-if="options.search || options.api" class="w-full space-y-3">
    <div class="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-400 dark:border-gray-700">
      <!-- Header título y controles responsive -->
      <div v-if="!options.noTitle" class="px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white">
          {{ options.title }}
        </h2>

        <!-- Control de vista en móvil -->
        <div class="flex items-center gap-2 md:hidden">
          <button
            class="text-xs px-2 py-1 rounded border border-gray-400 dark:border-gray-600 flex items-center gap-1"
            :class="{ 'bg-blue-50 dark:bg-slate-700': viewMode === 'cards' }"
            @click="toggleViewMode"
          >
            <component :is="viewMode === 'cards' ? iconos.ViewColumnsIcon : iconos.TableCellsIcon" class="size-3.5" />
            {{ viewMode === 'cards' ? 'Vista tarjetas' : 'Vista tabla' }}
          </button>
        </div>
      </div>

      <!-- Slot para header-top -->
      <slot name="header-top"></slot>

      <!-- Lista de datos - Vista tabla en pantalla grande / Vista tarjetas en móvil -->
      <div v-if="!options.loading">
        <!-- Vista tabla (visible en pantallas grandes o cuando se selecciona) -->
        <div v-if="!isMobileView || viewMode === 'table'" class="divide-y divide-gray-200 rounded-t-lg dark:divide-gray-700">
          <!-- Header de columnas -->
          <div class="px-6 py-2.5 bg-gray-50 rounded-t-lg dark:bg-gray-800 flex items-center min-h-12">
            <template v-if="options.showHeader">
              <!-- Checkbox del header más pequeño -->
              <div v-if="options.columnChecked" class="flex-none pr-4">
                <InputDefault
                  v-model="seleccionarAll" type="checkbox"
                  :disabled="options.isSelectable ? (options.rows as T[]).filter(r => options.isSelectable!(r as T)).length === 0 : false"
                  class="cursor-pointer h-3.5 w-3.5 rounded border-gray-400 text-blue-600 focus:ring-blue-600 dark:border-gray-600 dark:bg-gray-700 dark:checked:bg-blue-600 dark:focus:ring-offset-gray-800"
                  @change="checkedAll" />
              </div>

              <!-- Resto del header -->
              <div
                class="flex-1 min-w-0"
                :class="options.alinearColumnas ? 'grid' : 'flex'"
                :style="options.alinearColumnas ? estiloColumnasAlineadas : undefined"
              >
                <div
                  v-for="col in options.columns.filter(e => e.name != 'Action')" :key="col.name"
                  class="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center px-2 overflow-hidden"
                  :class="col.class"
                  :style="options.alinearColumnas ? undefined : getColumnStyle(col)">
                  <span class="w-full wrap-break-word whitespace-normal">{{
                    col.label
                  }}</span>
                </div>

                <div
                  v-if="options.alinearColumnas && options.columns.some(e => e.name == 'Action')"
                  class="flex items-center justify-center px-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {{ options.columns.find(e => e.name == 'Action')?.label }}
                </div>
              </div>

              <div
                v-if="!options.alinearColumnas && options.columns.some(e => e.name == 'Action')" class="flex-none text-center"
                  :style="getActionColumnStyle"></div>
            </template>
          </div>

          <!-- Filas de datos -->
          <div
            v-for="(row, index) in options.rows" :key="index"
            class="group hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors duration-200">
            <div class="px-6 py-3 flex items-center">
              <!-- Checkbox de fila más pequeño -->
              <div v-if="options.columnChecked" class="flex-none pr-4">
                <InputDefault
                  v-model="options.seleccionados" type="checkbox" :value="(row as T)[options.columnChecked]"
                  :disabled="options.isSelectable ? !options.isSelectable(row as T) : false"
                  class="cursor-pointer h-3.5 w-3.5 rounded border-gray-400 text-blue-600 focus:ring-blue-600 dark:border-gray-600 dark:bg-gray-700 dark:checked:bg-blue-600 dark:focus:ring-offset-gray-800" />
              </div>

              <!-- Contenido de la fila -->
              <div
                class="flex-1 min-w-0"
                :class="options.alinearColumnas ? 'grid' : 'flex'"
                :style="options.alinearColumnas ? estiloColumnasAlineadas : undefined"
              >
                <div
                  v-for="col in options.columns.filter(e => e.name != 'Action')" :key="col.name" :class="[
                  'min-w-0 flex items-center px-2 overflow-hidden',
                  col.expandable ? 'cursor-pointer hover:text-blue-600' : '',
                  col.class ?? ''
                ]" :style="options.alinearColumnas ? undefined : getColumnStyle(col)" @click="col.expandable ? toggleExpand(row as T) : null">
                  <!-- Modificar esta parte para el manejo de slots -->
                  <slot
                    :name="col.name"
                    v-bind="{
                      row,
                      col,
                      index,
                      toggleExpand,
                      isExpanded
                    }">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                      v-if="col.html" class="text-xs w-full overflow-hidden wrap-break-word whitespace-normal"
                      v-html="col.html(row as T)"></div>
                    <!-- eslint-enable vue/no-v-html -->
                    <component :is="col.component(row)" v-else-if="col.component" class="w-full overflow-hidden" />
                    <div v-else class="flex items-center gap-2 w-full overflow-hidden">
                      <span class="text-xs text-gray-900 dark:text-gray-300 wrap-break-word whitespace-normal w-full">
                        {{
                          col.mapValue
                            ? col.mapValue(row)
                            : (row as T)[col.name]
                        }}
                      </span>
                      <ChevronDownIcon
                        v-if="col.expandable"
                        class="size-4 transition-transform duration-200 shrink-0"
                        :class="{ 'rotate-180': isExpanded((row as T)) }" />
                    </div>
                  </slot>
                </div>
                <div
                  v-if="options.alinearColumnas && options.columns.some(e => e.name == 'Action')"
                  class="flex min-w-0 items-center justify-center px-2"
                >
                  <slot
                    name="Action"
                    v-bind="{
                      row,
                      col: options.columns.find(e => e.name == 'Action'),
                      iconos,
                      rows: options.rows,
                      toggleExpand,
                      isExpanded,
                      index
                    }"
                  >
                    <ButtonGroup
                      v-if="getButtonsActions(options.columns, (row as T)) && (getButtonsActions(options.columns, (row as T)) as object[]).length > 0"
                      :buttons="getButtonsActions(options.columns, (row as T))"
                    />
                  </slot>
                </div>
              </div>
              <div
                v-if="!options.alinearColumnas && options.columns.some(e => e.name == 'Action')" class="flex-none flex items-center justify-center"
                :style="getActionColumnStyle">
                <slot
                  name="Action"
                  v-bind="{
                    row,
                    col: options.columns.find(e => e.name == 'Action'),
                    iconos,
                    rows: options.rows,
                    toggleExpand,
                    isExpanded,
                    index
                  }">
                  <ButtonGroup
                    v-if="getButtonsActions(options.columns, (row as T)) && (getButtonsActions(options.columns, (row as T)) as object[]).length > 0"
                    :buttons="getButtonsActions(options.columns, (row as T))"
                  />
                </slot>
              </div>
            </div>

            <!-- Detalles expandibles -->
            <div
              v-if="isExpanded((row as T))"
              class="px-6 py-3 bg-gray-50 dark:bg-slate-800 border-t border-gray-100 dark:border-gray-700">
              <slot name="details" v-bind="{ row, isExpanded }">
                <!-- eslint-disable vue/no-v-html -->
                <div v-if="options.getDetails" v-html="options.getDetails(row)"></div>
                <!-- eslint-enable vue/no-v-html -->
              </slot>
            </div>
          </div>
        </div>

        <!-- Vista tarjetas (visible en pantallas pequeñas) -->
        <div v-else-if="viewMode === 'cards'" class="divide-y divide-gray-200 dark:divide-gray-700">
          <div
            v-for="(row, index) in options.rows"
            :key="index"
            class="p-4 group hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            <!-- Tarjeta para móvil -->
            <div class="flex justify-between items-start mb-2">
              <!-- Checkbox si existe -->
              <div v-if="options.columnChecked" class="flex-none">
                <InputDefault
                  v-model="options.seleccionados"
                  type="checkbox"
                  :value="(row as any)[options.columnChecked]"
                  :disabled="options.isSelectable ? !options.isSelectable(row as T) : false"
                  class="h-3.5 w-3.5 rounded"
                />
              </div>
            </div>

            <!-- Contenido de datos en formato vertical -->
            <div class="space-y-2">
              <div
                v-for="col in options.columns.filter(e => e.name != 'Action')"
                :key="col.name"
                :class="['flex flex-col text-sm', col.expandable ? 'cursor-pointer' : '']"
                @click="col.expandable ? toggleExpand((row as T)) : null"
              >
                <div class="flex items-center justify-between">
                  <span class="font-medium text-gray-700 dark:text-gray-300">{{ col.label }}:</span>
                </div>
                <div class="pl-2">
                  <slot
                    :name="col.name"
                    v-bind="{
                      row,
                      col,
                      index,
                      toggleExpand,
                      isExpanded
                    }"
                  >
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-if="col.html" v-html="col.html((row as T))"></div>
                    <!-- eslint-enable vue/no-v-html -->
                    <component :is="col.component(row)" v-else-if="col.component" />
                    <div v-else class="flex items-center gap-2 w-full overflow-hidden">
                      <span class="text-sm text-gray-900 dark:text-gray-300 wrap-break-word whitespace-normal w-full">
                        {{ col.mapValue ? col.mapValue(row) : (row as any)[col.name] }}
                      </span>
                    </div>
                  </slot>
                </div>
              </div>
            </div>

            <!-- Botones de acción en la parte inferior (solo para vista móvil) -->
            <div
              v-if="options.columns.some(e => e.name == 'Action') && (getButtonsActions(options.columns, row as T)?.length > 0 || $slots.Action)"
              class="mt-4 pt-3 flex justify-center items-center gap-2 border-t border-gray-100 dark:border-gray-700"
            >
              <slot
                name="Action"
                v-bind="{
                  row,
                  col: options.columns.find(e => e.name == 'Action'),
                  iconos,
                  rows: options.rows,
                  toggleExpand,
                  isExpanded,
                  index
                }">
                <ButtonGroup
                  v-if="getButtonsActions(options.columns, row as T) && (getButtonsActions(options.columns, (row as T)) as object[]).length > 0"
                  :buttons="getButtonsActions(options.columns, row as T)"
                />
              </slot>
            </div>

            <!-- Área expandible -->
            <div
              v-if="isExpanded(row as T)"
              class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700"
            >
              <slot name="details" v-bind="{ row, isExpanded }">
                <!-- eslint-disable vue/no-v-html -->
                <div v-if="options.getDetails" v-html="options.getDetails(row)"></div>
                <!-- eslint-enable vue/no-v-html -->
              </slot>
            </div>
          </div>
        </div>
      </div>

      <!-- Estados de carga y sin resultados -->
      <div v-if="options.loading" class="w-full">
        <div class="px-6 divide-y divide-gray-200 dark:divide-gray-700">
          <div v-for="i in 3" :key="i" class="animate-pulse">
            <!-- Card skeleton -->
            <div class="py-4">
              <div class="flex items-center gap-4">
                <!-- Checkbox skeleton -->
                <div v-if="options.columnChecked" class="flex-none">
                  <div class="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>

                <!-- Content area -->
                <div class="flex-1 space-y-3">
                  <!-- Main content skeleton -->
                  <div class="flex items-center gap-4">
                    <!-- Icon or avatar placeholder -->
                    <div class="shrink-0">
                      <div class="size-10 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                    </div>

                    <!-- Text content -->
                    <div class="flex-1 space-y-2">
                      <!-- Title bar -->
                      <div class="h-4 w-1/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <!-- Subtitle/tags area -->
                      <div class="flex gap-2">
                        <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action skeleton -->
                <div v-if="options.columns.some(e => e.name == 'Action')" class="flex-none">
                  <div class="size-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!options.loading && (!options.rows || !options.rows.length)" class="p-8 text-center">
        <alert-default texto="" :color="'blue'" titulo="Sin resultados" icono="MagnifyingGlassIcon" />
      </div>

      <!-- Paginación -->
      <div v-if="!options.noPagination" class="px-6 py-3 bg-gray-50 dark:bg-slate-800 rounded-b-lg border-t border-gray-400 dark:border-gray-700">
        <div class="flex flex-col xs:flex-row justify-between items-center gap-4">
          <!-- Info de paginación -->
          <div class="text-xs text-gray-600 dark:text-gray-400 text-center xs:text-left">
            <!-- Versión móvil simplificada -->
            <div class="xs:hidden flex flex-col items-center space-y-1">
              <span class="font-medium">{{ options.count }} resultados</span>
              <span>Página {{ options.pag }}/{{ options.countPag }}</span>
            </div>

            <!-- Versión desktop completa -->
            <p class="hidden xs:block">
              Mostrando página {{ options.pag }} de {{ options.countPag }}
              <span class="mx-1">•</span>
              {{ options.count }} resultados
            </p>
          </div>

          <div class="flex flex-col xs:flex-row items-center gap-2 w-full xs:w-auto">
            <!-- Selector de límite -->
            <div class="flex items-center gap-1 w-full xs:w-auto justify-between xs:justify-start">
              <span class="text-xs text-gray-600 dark:text-gray-400 xs:hidden">Por página:</span>
              <SelectDefault
                :id="'paginacion_' + options.identification" v-model="options.limit"
                :options="options.paginacionOpciones ?? [5, 10, 25, 100]" size="small" :basic-select="true" class="w-20! text-xs"
                @change="cambiarLimit" />
            </div>

            <!-- Controles de paginación -->
            <div class="flex gap-1 w-full xs:w-auto justify-center xs:justify-start mt-2 xs:mt-0">
              <data-table-pagination
                v-if="paginationOptions"
                :options="paginationOptions" @cambiar-pagina="changePage($event)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  type Ref,
  computed,
  type UnwrapRef,
  onBeforeUnmount,
  nextTick
} from 'vue'
import { filtrar, getPageCount } from '@/modules/Core/usings/generales.js'
import type {
  IDataTable,
  IPaginationOptions,
  IResponseApiSearchBody,
  IColumn,
  IButton
} from '@/modules/Core/interfaces'
import * as iconos from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import * as allServices from '@/services'
import { HSDropdown } from 'preline'

let observer: MutationObserver

const seleccionarAll = ref(false)
const props = defineProps<{
  options: IDataTable<T>
  updateTable?: (item: IDataTable<T>) => void
}>()
const options = ref(props.options)
const viewMode = ref('cards')
const isMobileView = ref(false)
const isInitialized = ref(false) // Flag para controlar búsquedas durante inicialización
const paginationOptions: Ref<IPaginationOptions | null> = ref(null)
const changePage = async (value: number) => {
  options.value.pag = value
  updatePagination()
  // No llamar reload aquí ya que el watcher de options.value.pag se encarga de ejecutar la búsqueda
}
const updatePagination = async () => {
  paginationOptions.value = null
  await nextTick()
  paginationOptions.value = {
    pag: options.value.pag,
    countPag: options.value.countPag
  }
}
updatePagination()
options.value.seleccionados = options.value.seleccionados || []
options.value.showHeader = options.value.showHeader ?? true
// Propiedades computadas para gestión de anchos de columna
const columnsWithoutWidth = computed(() => {
  return options.value.columns.filter(
    col => col.name !== 'Action' && !col.width
  ).length
})

const availableSpace = computed(() => {
  let usedPercentage = 0
  let pixelColumnsCount = 0
  const dataColumns = options.value.columns.filter(col => col.name !== 'Action')

  dataColumns.forEach(col => {
    if (col.width) {
      if (typeof col.width === 'string' && col.width.endsWith('%')) {
        usedPercentage += parseFloat(col.width)
      } else if (typeof col.width === 'number') {
        pixelColumnsCount++
      }
    }
  })
  const estimatedPixelSpace = pixelColumnsCount * 5
  const remainingSpace = Math.max(0, 100 - usedPercentage - estimatedPixelSpace)

  return remainingSpace
})

const autoColumnWidth = computed(() => {
  if (columnsWithoutWidth.value === 0) return 'auto'
  return `${availableSpace.value / columnsWithoutWidth.value}%`
})

const estiloColumnasAlineadas = computed(() => {
  const columnas = options.value.columns.map(columna => {
    if (columna.name === 'Action') {
      if (typeof columna.width === 'number') return `${columna.width}px`
      return columna.width || '60px'
    }

    if (typeof columna.width === 'string' && columna.width.endsWith('%')) {
      return `minmax(0, ${parseFloat(columna.width)}fr)`
    }

    if (typeof columna.width === 'number') return `minmax(0, ${columna.width}px)`
    return 'minmax(0, 1fr)'
  })

  return { gridTemplateColumns: columnas.join(' ') }
})

const getColumnStyle = (col: IColumn<T>) => {
  const baseStyle: Record<string, string> = {
    width:
      typeof col.width === 'number'
        ? `${col.width}px`
        : col.width || autoColumnWidth.value,
    minWidth: '0',
    overflow: 'hidden'
  }
  return baseStyle
}

const getActionColumnStyle = computed(() => {
  const actionColumn = options.value.columns.find(e => e.name === 'Action')
  const baseStyle: Record<string, string> = {
    width: actionColumn?.width
      ? typeof actionColumn.width === 'number'
        ? `${actionColumn.width}px`
        : actionColumn.width
      : '60px',
    minWidth: '60px',
    flexShrink: '0'
  }
  return baseStyle
})

const getButtonsActions = (columns : IColumn<T>[], row : T) => {
  const actionColumn = columns.find(e => e.name == 'Action') as IColumn<T>;
  if (!actionColumn || !actionColumn.btns) return [];

  // Asegurarse de que todos los botones tengan la propiedad 'show' definida para ButtonGroup
  return actionColumn.btns
    .map(btn => {
      // Crear una función click que esté vinculada a la fila actual
      const originalClick = btn.click;
      const boundClick = () => originalClick(row);

      return {
        ...btn,
        // Si no tiene show, establecerlo como true por defecto
        show: () => btn.show ? btn.show(row) : true,
        // Reemplazar la función click por la versión enlazada
        click: btn.expanded?.(row) ? () => expanded(row) : boundClick,
        // Asegurar que el botón tiene la clase correcta si no está definida
        class: btn.class || 'p-1.5 inline-flex items-center justify-center rounded-md bg-white text-gray-800 hover:bg-gray-50 focus:outline-none'
      };
    })
    .filter(btn => btn.show() !== false) as IButton<unknown>[]; // Solo mostrar botones con show=true
}
watch(() => props.options, async (nuevasPropiedades: IDataTable<T>) => {
  // Solo actualizar si las props han cambiado realmente
  if (JSON.stringify(options.value) !== JSON.stringify(nuevasPropiedades)) {
    options.value = nuevasPropiedades
    if (options.value.isList) options.value.count = options.value.rows.length
    await updatePagination()
  }
}, { deep: true })

watch(() => options.value.pag, async (newPag, oldPag) => {
  // Solo ejecutar búsqueda si ya está inicializado y realmente cambió la página, y no está cambiando límite
  if (isInitialized.value && newPag !== oldPag && newPag !== undefined && !isChangingLimit.value) {
    await ejecuteSearch()
  }
})

const expandedRows = ref(new Set())
const emit = defineEmits(['toggle-expand', 'is-expanded'])
const isExpanded = (row: T) => {
  emit('is-expanded', row)
  return expandedRows.value.has(row)
}

watch(
  () => options.value.rows,
  newRows => {
    // Resetear selección al recargar filas
    seleccionarAll.value = false
    if (options.value.seleccionados) options.value.seleccionados = []

    if (!options.value.autoExpand) return
    expandedRows.value.clear()
    newRows.forEach(row => {
      if (options.value.autoExpand!(row as T)) {
        expandedRows.value.add(row)
      }
    })
  },
  { immediate: true }
)

const toggleExpand = (row: T) => {
  if (isExpanded(row)) {
    expandedRows.value.delete(row)
  } else {
    expandedRows.value.add(row)
  }
  emit('toggle-expand', row)
}

const expanded = (row: T) => {
  toggleExpand(row)
}

const checkedAll = () => {
  const columnKey = options.value.columnChecked
  if (!columnKey) return
  const selectableRows = options.value.isSelectable
    ? (options.value.rows as T[]).filter((row: T) => options.value.isSelectable!(row))
    : options.value.rows
  options.value.seleccionados = seleccionarAll.value
    ? selectableRows.map(item => item[columnKey])
    : []
}
const isChangingLimit = ref(false)
const cambiarLimit = async () => {
  isChangingLimit.value = true
  // Reset a la primera página cuando cambia el límite
  options.value.pag = 1
  await ejecuteSearch()
  isChangingLimit.value = false
}
const initializeDropdowns = () => {
  if (typeof window !== 'undefined') {
    HSDropdown.autoInit()
  }
}
const ejecuteSearch = async () => {
  if (options.value.noAutoSearch || options.value.loading) return

  options.value.loading = true
  try {
    if (options.value.search) {
      const result = await options.value.search()
      calculatePagination(result)
    } else if (options.value.api) {
      const serviceKey = options.value.api as keyof typeof allServices
      const service = allServices[serviceKey]

      if (service && typeof service.search === 'function') {
        const params = filtrar(
          options.value.filtros,
          options.value.pag,
          options.value.limit
        )
        const { data } = await service.search(params)
        calculatePagination(data.body as IResponseApiSearchBody<T>)
      } else {
        console.error(
          `El servicio "${options.value.api}" no existe o no tiene un método search`
        )
      }
    }
  } catch (error) {
    console.error('Error al ejecutar la búsqueda:', error)
  } finally {
    options.value.loading = false
  }
}

options.value.reload = () => {
  options.value.pag = 1
  ejecuteSearch()
}
onMounted(async () => {
  await ejecuteSearch()
  isInitialized.value = true // Marcar como inicializado después de la primera búsqueda
  initializeDropdowns()

  observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length) {
        initializeDropdowns()
      }
    })
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true
  })

  // detección de tamaño de pantalla
  checkMobileView()
  window.addEventListener('resize', checkMobileView)
})

const calculatePagination = (result: IResponseApiSearchBody<unknown> | T[]) => {
  if (isResponseApiSearchBody(result as IResponseApiSearchBody<unknown>)) {
    const resultSearch = result as IResponseApiSearchBody<unknown>
    options.value.countPag = getPageCount(
      options.value.limit,
      resultSearch.count
    )
    options.value.rows = resultSearch.rows as UnwrapRef<T[]>
    options.value.count = resultSearch.count
  } else if (result && Array.isArray(result)) {

    options.value.countPag = Math.ceil(
      (result as unknown[]).length / options.value.limit
    )
    options.value.rows = (result as UnwrapRef<T[]>).slice(
      (options.value.pag - 1) * options.value.limit,
      options.value.pag * options.value.limit
    )
    options.value.count = (result as unknown[]).length
  } else {
    options.value.countPag = 0
    options.value.rows = [] as unknown as UnwrapRef<T[]>
    options.value.count = 0
  }
  updatePagination()
}

const isResponseApiSearchBody = (
  result: IResponseApiSearchBody<unknown>
): boolean => {
  return (
    result &&
    typeof result === 'object' &&
    'count' in result &&
    typeof result.count === 'number' &&
    'rows' in result &&
    Array.isArray(result.rows)
  )
}

const toggleViewMode = () => {
  viewMode.value = viewMode.value === 'cards' ? 'table' : 'cards'
}

const checkMobileView = () => {
  isMobileView.value = window.innerWidth < 768
}

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobileView)
})

defineExpose({
  expanded,
  isExpanded,
  toggleExpand
})
</script>
